/** @format */

export interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  requirements: {
    id: string;
    label: string;
    met: boolean;
  }[];
}

/**
 * Password requirements configuration
 */
export const passwordRequirements: PasswordRequirement[] = [
  {
    id: 'minLength',
    label: 'At least 8 characters',
    test: (password) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'At least one uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'At least one lowercase letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    label: 'At least one number',
    test: (password) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'At least one special character (!@#$%^&*)',
    // eslint-disable-next-line no-useless-escape
    test: (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  },
];

/**
 * Validates a password against all requirements
 * @param password - The password to validate
 * @returns PasswordValidationResult with overall validity and individual requirement status
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const results = passwordRequirements.map((req) => ({
    id: req.id,
    label: req.label,
    met: req.test(password),
  }));

  return {
    isValid: results.every((r) => r.met),
    requirements: results,
  };
};

/**
 * Gets the password strength as a percentage
 * @param password - The password to check
 * @returns A number between 0 and 100
 */
export const getPasswordStrength = (password: string): number => {
  const result = validatePassword(password);
  const metCount = result.requirements.filter((r) => r.met).length;
  return Math.round((metCount / result.requirements.length) * 100);
};

/**
 * Gets a strength label based on percentage
 * @param strength - The strength percentage
 * @returns A label: 'Weak', 'Fair', 'Good', or 'Strong'
 */
export const getStrengthLabel = (strength: number): 'Weak' | 'Fair' | 'Good' | 'Strong' => {
  if (strength < 40) return 'Weak';
  if (strength < 60) return 'Fair';
  if (strength < 100) return 'Good';
  return 'Strong';
};

/**
 * Gets the color for the strength indicator
 * @param strength - The strength percentage
 * @returns A color string
 */
export const getStrengthColor = (strength: number): string => {
  if (strength < 40) return '#ff4d4f';
  if (strength < 60) return '#faad14';
  if (strength < 100) return '#52c41a';
  return '#52c41a';
};
